import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  addDays,
  addMonths,
  subMonths,
  isToday,
  isSameMonth,
} from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  getMissionColor,
  getReadableTextColor,
  getMissionMinuteRange,
  getActiviteIcon,
  formatHHmmFromMinutes,
} from '../services/calendarHelpers';
import './Calendar.css';

const HOUR_HEIGHT_PX = 56;
const DAY_VIEW_START_HOUR = 6;
const DAY_VIEW_END_HOUR = 22;
const VISIBLE_HOURS = DAY_VIEW_END_HOUR - DAY_VIEW_START_HOUR;
const TOTAL_GRID_HEIGHT = VISIBLE_HOURS * HOUR_HEIGHT_PX;

const MIN_SWIPE_DISTANCE = 60;

// ---------------------------------------------------------------------------
// Helpers internes
// ---------------------------------------------------------------------------

const formatDateKey = (date) => format(date, 'yyyy-MM-dd');

const getMissionDateKey = (mission) => {
  const candidate =
    mission.date_affectation ||
    mission.date_prevue ||
    mission.datePrevue ||
    mission.date_inspection ||
    mission.date_verification ||
    null;
  if (!candidate) return null;
  const d = new Date(candidate);
  if (Number.isNaN(d.getTime())) return null;
  return formatDateKey(d);
};

// Affecte chaque event a une colonne pour gerer les chevauchements (algo simple)
const layoutDayEvents = (events) => {
  const sorted = [...events].sort((a, b) => {
    const dA = a.startMinutes - b.startMinutes;
    if (dA !== 0) return dA;
    return a.endMinutes - b.endMinutes;
  });

  const clusters = [];
  for (const ev of sorted) {
    let placed = false;
    for (const cluster of clusters) {
      const overlaps = cluster.events.some(
        (e) => e.startMinutes < ev.endMinutes && e.endMinutes > ev.startMinutes,
      );
      if (overlaps) {
        const usedCols = new Set(
          cluster.events
            .filter((e) => e.startMinutes < ev.endMinutes && e.endMinutes > ev.startMinutes)
            .map((e) => e.col),
        );
        let col = 0;
        while (usedCols.has(col)) col += 1;
        ev.col = col;
        cluster.events.push(ev);
        cluster.colCount = Math.max(cluster.colCount, col + 1);
        placed = true;
        break;
      }
    }
    if (!placed) {
      ev.col = 0;
      clusters.push({ events: [ev], colCount: 1 });
    }
  }

  return sorted.map((ev) => {
    const cluster = clusters.find((c) => c.events.includes(ev));
    return { ...ev, colCount: cluster?.colCount || 1 };
  });
};

// Construit une liste d'events affichables avec leurs positions (minutes -> px)
const buildPositionedEvents = (missions) => {
  const enriched = missions
    .map((mission) => {
      const range = getMissionMinuteRange(mission);
      if (!range) return null;
      // On ne veut que la fenetre visible : on clamp
      const startMinutes = Math.max(range.startMinutes, DAY_VIEW_START_HOUR * 60);
      const endMinutes = Math.min(range.endMinutes, DAY_VIEW_END_HOUR * 60);
      if (endMinutes <= startMinutes) return null;
      return {
        mission,
        startMinutes,
        endMinutes,
        dureeMinutes: endMinutes - startMinutes,
        color: getMissionColor(mission),
      };
    })
    .filter(Boolean);
  return layoutDayEvents(enriched);
};

const minutesToPx = (minutes) => ((minutes - DAY_VIEW_START_HOUR * 60) / 60) * HOUR_HEIGHT_PX;

// ---------------------------------------------------------------------------
// Sous-composants : Event Block (semaine/jour), Tooltip, etc.
// ---------------------------------------------------------------------------

const EventBlock = ({ ev, onClick, dense }) => {
  const top = minutesToPx(ev.startMinutes);
  const height = Math.max(20, ((ev.endMinutes - ev.startMinutes) / 60) * HOUR_HEIGHT_PX - 2);
  const widthPct = 100 / ev.colCount;
  const leftPct = ev.col * widthPct;
  const textColor = getReadableTextColor(ev.color);
  const heureDebut = formatHHmmFromMinutes(ev.startMinutes);
  const heureFin = formatHHmmFromMinutes(ev.endMinutes);

  return (
    <div
      className="cal-event-block"
      style={{
        top: `${top}px`,
        height: `${height}px`,
        left: `${leftPct}%`,
        width: `calc(${widthPct}% - 4px)`,
        background: ev.color,
        color: textColor,
      }}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(ev.mission);
      }}
    >
      <div className="cal-event-time">
        {getActiviteIcon(ev.mission.activite)} {heureDebut} - {heureFin}
      </div>
      <div className="cal-event-title">{ev.mission.client_nom || ev.mission.numero_rapport}</div>
      {!dense && ev.mission.etablissement && (
        <div className="cal-event-sub">{ev.mission.etablissement}</div>
      )}
      {!dense && ev.mission.verificateur && (
        <div className="cal-event-sub">👤 {ev.mission.verificateur}</div>
      )}
    </div>
  );
};

const NowIndicator = ({ visible }) => {
  if (!visible) return null;
  const now = new Date();
  const minutesNow = now.getHours() * 60 + now.getMinutes();
  if (minutesNow < DAY_VIEW_START_HOUR * 60 || minutesNow > DAY_VIEW_END_HOUR * 60) return null;
  const top = minutesToPx(minutesNow);
  return (
    <div className="cal-now-line" style={{ top: `${top}px` }}>
      <div className="cal-now-dot" />
    </div>
  );
};

const HourLabels = () => (
  <div className="cal-hour-labels">
    {Array.from({ length: VISIBLE_HOURS + 1 }).map((_, i) => (
      <div key={i} className="cal-hour-label" style={{ top: `${i * HOUR_HEIGHT_PX}px` }}>
        {String(DAY_VIEW_START_HOUR + i).padStart(2, '0')}:00
      </div>
    ))}
  </div>
);

const HourGridLines = () => (
  <div className="cal-hour-grid-lines">
    {Array.from({ length: VISIBLE_HOURS }).map((_, i) => (
      <div key={i} className="cal-hour-grid-line" style={{ top: `${(i + 1) * HOUR_HEIGHT_PX}px` }} />
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Composant principal
// ---------------------------------------------------------------------------

function Calendar({ missions, onMissionClick }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('week'); // 'month' | 'week' | 'day'
  const [missionsByDate, setMissionsByDate] = useState({});
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  // tickAt force un re-render toutes les minutes pour repositionner la now-line
  const [tickAt, setTickAt] = useState(Date.now());

  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const interval = setInterval(() => setTickAt(Date.now()), 60_000);
    return () => clearInterval(interval);
  }, []);

  const groupMissionsByDate = useCallback(() => {
    const grouped = {};
    if (Array.isArray(missions)) {
      missions.forEach((mission) => {
        const dateKey = getMissionDateKey(mission);
        if (!dateKey) return;
        if (!grouped[dateKey]) grouped[dateKey] = [];
        grouped[dateKey].push(mission);
      });
    }
    setMissionsByDate(grouped);
  }, [missions]);

  useEffect(() => {
    groupMissionsByDate();
  }, [groupMissionsByDate]);

  // Auto-scroll a 7h au montage des vues semaine/jour
  useEffect(() => {
    if ((viewMode === 'week' || viewMode === 'day') && scrollContainerRef.current) {
      const targetMinutes = Math.max(
        DAY_VIEW_START_HOUR * 60,
        new Date().getHours() * 60 + new Date().getMinutes() - 60,
      );
      scrollContainerRef.current.scrollTop = minutesToPx(targetMinutes);
    }
  }, [viewMode]);

  // ---------------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------------

  const navigate = (direction) => {
    const offset = direction === 'next' ? 1 : -1;
    if (viewMode === 'day') setCurrentDate(addDays(currentDate, offset));
    else if (viewMode === 'week') setCurrentDate(addDays(currentDate, 7 * offset));
    else if (viewMode === 'month') setCurrentDate(direction === 'next' ? addMonths(currentDate, 1) : subMonths(currentDate, 1));
  };

  const goToToday = () => setCurrentDate(new Date());

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);
  const onTouchEnd = () => {
    if (touchStart === null || touchEnd === null) return;
    const distance = touchStart - touchEnd;
    if (distance > MIN_SWIPE_DISTANCE) navigate('next');
    else if (distance < -MIN_SWIPE_DISTANCE) navigate('prev');
  };

  // ---------------------------------------------------------------------------
  // Vue MOIS
  // ---------------------------------------------------------------------------

  const renderMonthView = () => {
    const startGrid = startOfWeek(startOfMonth(currentDate), { weekStartsOn: 1 });
    const endGrid = endOfWeek(endOfMonth(currentDate), { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start: startGrid, end: endGrid });
    const weekDayHeaders = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

    return (
      <div className="cal-month-view" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
        <div className="cal-month-title">
          {format(currentDate, 'MMMM yyyy', { locale: fr })}
        </div>
        <div className="cal-month-headers">
          {weekDayHeaders.map((d) => (
            <div key={d} className="cal-month-header-cell">{d}</div>
          ))}
        </div>
        <div className="cal-month-grid">
          {days.map((day) => {
            const dateKey = formatDateKey(day);
            const missionsOfDay = missionsByDate[dateKey] || [];
            const inCurrentMonth = isSameMonth(day, currentDate);
            const isCurrent = isToday(day);
            return (
              <div
                key={dateKey}
                className={`cal-month-cell ${inCurrentMonth ? 'is-current-month' : 'is-other-month'} ${isCurrent ? 'is-today' : ''}`}
                onClick={() => {
                  setCurrentDate(day);
                  setViewMode('day');
                }}
              >
                <div className="cal-month-cell-date">{format(day, 'd')}</div>
                <div className="cal-month-cell-events">
                  {missionsOfDay.slice(0, 3).map((m) => {
                    const color = getMissionColor(m);
                    const textColor = getReadableTextColor(color);
                    return (
                      <div
                        key={m.id}
                        className="cal-month-event"
                        style={{ background: color, color: textColor }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onMissionClick?.(m);
                        }}
                      >
                        <span className="cal-month-event-time">
                          {m.heure_debut || ''}
                        </span>
                        <span className="cal-month-event-title">
                          {m.client_nom || m.numero_rapport}
                        </span>
                      </div>
                    );
                  })}
                  {missionsOfDay.length > 3 && (
                    <div className="cal-month-event-more">+{missionsOfDay.length - 3}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Vue SEMAINE
  // ---------------------------------------------------------------------------

  const renderWeekView = () => {
    const start = startOfWeek(currentDate, { weekStartsOn: 1 });
    const end = endOfWeek(currentDate, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start, end });

    return (
      <div className="cal-week-view" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
        <div className="cal-week-title">
          Semaine du {format(start, 'd MMM', { locale: fr })} au {format(end, 'd MMM yyyy', { locale: fr })}
        </div>

        <div className="cal-week-day-headers">
          <div className="cal-week-spacer" />
          {days.map((day) => (
            <div
              key={formatDateKey(day)}
              className={`cal-week-day-header ${isToday(day) ? 'is-today' : ''}`}
              onClick={() => {
                setCurrentDate(day);
                setViewMode('day');
              }}
            >
              <div className="cal-week-day-name">{format(day, 'EEE', { locale: fr })}</div>
              <div className="cal-week-day-number">{format(day, 'd')}</div>
            </div>
          ))}
        </div>

        <div className="cal-week-grid-scroll" ref={scrollContainerRef}>
          <div className="cal-week-grid" style={{ height: `${TOTAL_GRID_HEIGHT}px` }}>
            <HourLabels />
            <div className="cal-week-day-columns">
              {days.map((day) => {
                const dateKey = formatDateKey(day);
                const missionsOfDay = missionsByDate[dateKey] || [];
                const positionedEvents = buildPositionedEvents(missionsOfDay);
                return (
                  <div key={dateKey} className={`cal-week-day-column ${isToday(day) ? 'is-today' : ''}`} style={{ height: `${TOTAL_GRID_HEIGHT}px` }}>
                    <HourGridLines />
                    {positionedEvents.map((ev) => (
                      <EventBlock key={ev.mission.id} ev={ev} onClick={onMissionClick} dense />
                    ))}
                    {isToday(day) && <NowIndicator key={`now-week-${tickAt}`} visible />}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Vue JOUR
  // ---------------------------------------------------------------------------

  const renderDayView = () => {
    const dateKey = formatDateKey(currentDate);
    const missionsOfDay = missionsByDate[dateKey] || [];
    const positionedEvents = buildPositionedEvents(missionsOfDay);

    return (
      <div className="cal-day-view" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
        <div className="cal-day-title">
          {format(currentDate, 'EEEE d MMMM yyyy', { locale: fr })}
          {isToday(currentDate) && <span className="cal-day-today-badge">Aujourd'hui</span>}
        </div>

        {missionsOfDay.length === 0 ? (
          <div className="cal-empty-day">
            <p>📅 Aucune mission prevue ce jour</p>
            <p className="cal-empty-hint">Faites glisser horizontalement pour changer de jour</p>
          </div>
        ) : null}

        <div className="cal-day-grid-scroll" ref={scrollContainerRef}>
          <div className="cal-day-grid" style={{ height: `${TOTAL_GRID_HEIGHT}px` }}>
            <HourLabels />
            <div className="cal-day-content">
              <HourGridLines />
              {positionedEvents.map((ev) => (
                <EventBlock key={ev.mission.id} ev={ev} onClick={onMissionClick} dense={false} />
              ))}
              {isToday(currentDate) && <NowIndicator key={`now-day-${tickAt}`} visible />}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Header / View Switcher
  // ---------------------------------------------------------------------------

  return (
    <div className="cal-container">
      <div className="cal-header">
        <div className="cal-nav-row">
          <button className="cal-nav-button" onClick={() => navigate('prev')} aria-label="Precedent">◀</button>
          <button className="cal-today-button" onClick={goToToday}>Aujourd'hui</button>
          <button className="cal-nav-button" onClick={() => navigate('next')} aria-label="Suivant">▶</button>
        </div>
        <div className="cal-view-switcher">
          <button
            className={`cal-view-btn ${viewMode === 'month' ? 'is-active' : ''}`}
            onClick={() => setViewMode('month')}
          >
            Mois
          </button>
          <button
            className={`cal-view-btn ${viewMode === 'week' ? 'is-active' : ''}`}
            onClick={() => setViewMode('week')}
          >
            Semaine
          </button>
          <button
            className={`cal-view-btn ${viewMode === 'day' ? 'is-active' : ''}`}
            onClick={() => setViewMode('day')}
          >
            Jour
          </button>
        </div>
      </div>
      <div className="cal-body">
        {viewMode === 'month' && renderMonthView()}
        {viewMode === 'week' && renderWeekView()}
        {viewMode === 'day' && renderDayView()}
      </div>
    </div>
  );
}

export default Calendar;
